import { useSyncExternalStore } from "react";

/* ── The page's name, published for the breadcrumb ─────────────────────────

   The bar's last crumb and this header's page name used to be two lists of
   names kept by hand: the crumb from the menu (with its own overrides), the
   header from whatever the page passed. They disagreed on three pages at
   once — "Worksheets" over OFFERS, "Settings" over PROJECT SETTINGS,
   "Integrations" over LEGACY INTEGRATION (design review, 2026-10-02). The page
   is the authority on its own name, so the header publishes the name it
   SHOWS and Breadcrumbs reads it for the step you are on. Keyed by path, so a
   name can never outlive the page that set it.

   Lives here rather than in ui/page-header.tsx: Breadcrumbs is in the
   always-loaded shell, and importing the header from it put the header and
   its actions row into every visitor's first load. */
export type PublishedTitle = { path: string; title: string } | null
let publishedTitle: PublishedTitle = null
const titleListeners = new Set<() => void>()
export function publishTitle(next: PublishedTitle) {
  if (next?.path === publishedTitle?.path && next?.title === publishedTitle?.title) return
  publishedTitle = next
  titleListeners.forEach((l) => l())
}
/** The page name the open page's header shows, when it is for `path`. */
export function usePublishedPageTitle(path: string): string | null {
  const t = useSyncExternalStore(
    (cb) => {
      titleListeners.add(cb)
      return () => titleListeners.delete(cb)
    },
    () => publishedTitle,
    () => null,
  )
  return t && t.path === path ? t.title : null
}

/** Withdraw `title` for `path` — only if it is still the one published, so a
    page that unmounts after the next one has published cannot erase it. */
export function withdrawTitle(path: string, title: string) {
  if (publishedTitle?.path === path && publishedTitle.title === title) publishTitle(null)
}
